import { useReducer } from 'react';

import {
  Action,
  CLEAR_SEARCH,
  CLOSE_MENU,
  GO_TO_ITEM,
  OPEN_MENU,
  REGISTER_ITEM,
  SEARCH,
  UNREGISTER_ITEM,
} from '../actions';
import { MenuState } from '../MenuContext';
import { getIndex } from '../utils/getIndex';

export function useMenuReducer(initialState) {
  const reducer = (state: MenuState, action: Action): MenuState => {
    switch (action.type) {
      case CLOSE_MENU:
        if (state.isOpen === false) return state;
        return { ...state, isOpen: false, activeItemIndex: null };

      case OPEN_MENU:
        if (state.isOpen === true) return state;
        return { ...state, isOpen: true };

      case GO_TO_ITEM: {
        const activeItemIndex = getIndex(action, {
          resolveItems: () => state.items,
          resolveActiveIndex: () => state.activeItemIndex,
          resolveId: (item) => item.id,
          resolveDisabled: (item) => item.dataRef.current.disabled,
        });

        if (
          state.searchQuery === '' &&
          state.activeItemIndex === activeItemIndex
        )
          return state;

        return { ...state, searchQuery: '', activeItemIndex };
      }

      case SEARCH: {
        const searchQuery = state.searchQuery + action.value.toLowerCase();
        const match = state.items.findIndex(
          (item) =>
            item.dataRef.current.text?.startsWith(searchQuery) &&
            !item.dataRef.current.disabled
        );

        if (match === -1 || match === state.activeItemIndex)
          return { ...state, searchQuery };

        return { ...state, searchQuery, activeItemIndex: match };
      }

      case CLEAR_SEARCH:
        if (state.searchQuery === '') return state;
        return { ...state, searchQuery: '' };

      case REGISTER_ITEM:
        return {
          ...state,
          items: [...state.items, { id: action.id, dataRef: action.dataRef }],
        };

      case UNREGISTER_ITEM: {
        const nextItems = state.items.slice();
        const currentActiveItem =
          state.activeItemIndex !== null
            ? nextItems[state.activeItemIndex]
            : null;
        const idx = nextItems.findIndex((a) => a.id === action.id);

        if (idx !== -1) nextItems.splice(idx, 1);

        return {
          ...state,
          items: nextItems,
          activeItemIndex: (() => {
            if (idx === state.activeItemIndex) return null;
            if (currentActiveItem === null) return null;

            // Find the correct index if an item before the active item was removed.
            return nextItems.indexOf(currentActiveItem);
          })(),
        };
      }

      default:
        return state;
    }
  };

  return useReducer(reducer, initialState);
}
